import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useFeatures } from '../services/features';
import {
    FaSignOutAlt,
    FaBars,
    FaTimes,
    FaKeyboard,
    FaCheckCircle,
    FaCogs,
    FaChartBar,
    FaWarehouse,
    FaFileInvoiceDollar,
    FaKey,
    FaUsers,
    FaSolarPanel,
    FaCalendarPlus,
    FaBalanceScale
} from 'react-icons/fa';

const Sidebar = () => {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user'));
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const features = useFeatures();

    const handleLogout = () => {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        navigate('/login');
    };

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const closeMobileMenu = () => {
        setMobileMenuOpen(false);
    };

    const navLinkClass = ({ isActive }) =>
        `flex items-center px-3 py-2 text-sm transition-colors rounded-md mx-2 ${
            isActive
                ? 'bg-softTeal text-white [&>svg]:text-white'
                : 'text-gray-700 hover:bg-gray-100 [&>svg]:text-softTeal'
        }`;

    const sectionHeaderClass = "px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider mt-3 first:mt-0";

    const SidebarContent = ({ onLinkClick }) => (
        <>
            {/* Logo */}
            <div className="p-3 border-b border-gray-100">
                <NavLink to="/" onClick={onLinkClick}>
                    <img src="/images/logo.jpg" alt="Megawat" className="w-full" />
                </NavLink>
            </div>

            {/* Navigation */}
            <nav className="flex-1 py-2 overflow-y-auto">
                {/* DANE - tylko admin */}
                {user.role === 'admin' && (
                    <>
                        <div className={sectionHeaderClass}>Dane</div>
                        <NavLink to="/data-entry" className={navLinkClass} onClick={onLinkClick}>
                            <FaKeyboard className="mr-3" />
                            Wprowadzanie danych
                        </NavLink>
                        <NavLink to="/all-data-points" className={navLinkClass} onClick={onLinkClick}>
                            <FaCheckCircle className="mr-3" />
                            Zatwierdzone dane
                        </NavLink>
                        <NavLink to="/process-data" className={navLinkClass} onClick={onLinkClick}>
                            <FaCogs className="mr-3" />
                            Przetwarzanie danych
                        </NavLink>
                    </>
                )}

                {/* RAPORTY - wszyscy */}
                <div className={sectionHeaderClass}>Raporty</div>
                <NavLink to="/reports" className={navLinkClass} onClick={onLinkClick}>
                    <FaChartBar className="mr-3" />
                    Generowanie raportów
                </NavLink>
                <NavLink to="/storage-state" className={navLinkClass} onClick={onLinkClick}>
                    <FaWarehouse className="mr-3" />
                    Stan magazynu
                </NavLink>

                {/* FAKTURY - wszyscy, o ile instancja ma włączony moduł fakturowania */}
                {features?.invoicing && (
                    <>
                        <div className={sectionHeaderClass}>Faktury</div>
                        <NavLink to="/ksef-token" className={navLinkClass} onClick={onLinkClick}>
                            <FaKey className="mr-3" />
                            Token KSeF
                        </NavLink>
                        <NavLink to="/invoices" className={navLinkClass} onClick={onLinkClick}>
                            <FaFileInvoiceDollar className="mr-3" />
                            Faktury
                        </NavLink>
                        {user.role === 'admin' && (
                            <>
                                <NavLink to="/billing-runs" className={navLinkClass} onClick={onLinkClick}>
                                    <FaCalendarPlus className="mr-3" />
                                    Generowanie faktur
                                </NavLink>
                                <NavLink to="/balancing" className={navLinkClass} onClick={onLinkClick}>
                                    <FaBalanceScale className="mr-3" />
                                    Bilansowanie
                                </NavLink>
                            </>
                        )}
                    </>
                )}

                {/* ADMINISTRACJA - tylko admin */}
                {user.role === 'admin' && (
                    <>
                        <div className={sectionHeaderClass}>Administracja</div>
                        <NavLink to="/admin" end className={navLinkClass} onClick={onLinkClick}>
                            <FaUsers className="mr-3" />
                            Użytkownicy
                        </NavLink>
                        <NavLink to="/admin/devices" className={navLinkClass} onClick={onLinkClick}>
                            <FaSolarPanel className="mr-3" />
                            PPE
                        </NavLink>
                    </>
                )}
            </nav>

            {/* User info & Logout */}
            <div className="p-3 border-t border-gray-100">
                <div className="flex items-center mb-2">
                    <div className="w-7 h-7 bg-softTeal rounded-full flex items-center justify-center text-white font-semibold text-xs">
                        {user.username.charAt(0).toUpperCase()}
                    </div>
                    <div className="ml-2">
                        <p className="text-sm font-semibold text-gray-700">{user.username}</p>
                        <p className="text-xs text-gray-400">{user.role}</p>
                    </div>
                </div>
                <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                >
                    <FaSignOutAlt className="mr-2" />
                    Wyloguj
                </button>
            </div>
        </>
    );

    return (
        <>
            {/* Mobile Header */}
            <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white shadow-sm px-4 py-3 flex items-center justify-between">
                <NavLink to="/">
                    <img src="/images/logo.jpg" alt="Megawat" className="h-10" />
                </NavLink>
                <button
                    onClick={toggleMobileMenu}
                    className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                    aria-label="Toggle menu"
                >
                    {mobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
                </button>
            </div>

            {/* Mobile Sidebar Overlay */}
            {mobileMenuOpen && (
                <div
                    className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
                    onClick={closeMobileMenu}
                />
            )}

            {/* Mobile Sidebar */}
            <aside className={`
                md:hidden fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50
                transform transition-transform duration-300 ease-in-out
                ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
            `}>
                <div className="flex flex-col h-full">
                    <SidebarContent onLinkClick={closeMobileMenu} />
                </div>
            </aside>

            {/* Desktop Sidebar */}
            <aside className="hidden md:flex md:flex-col md:w-64 md:fixed md:inset-y-0 bg-white shadow-lg z-30">
                <SidebarContent onLinkClick={() => {}} />
            </aside>
        </>
    );
};

export default Sidebar;
